import { MaintenanceEventAttributes, MaintenanceEventState } from '@/types'
import { UpdateMaintenanceEventMutation } from './__generated__/update_mutation'
import { Mutation as UpdateMutation } from './update_mutation'
import { useForm } from '@/helpers/useForm'
import { EditMaintenanceEventQuery } from './__generated__/edit_query'
import { Query as EditQuery } from './edit_query'
import { Inline as Loading } from '@/components/loading'
import { Inline as Error } from '@/components/error'
import { Field, Label } from '../base/fieldset'
import { Input } from '../base/input'
import { Button } from '../base/button'

type MaintenanceEvent = EditMaintenanceEventQuery['maintenanceEvents']['nodes'][0]

interface Props {
  id: string
  onSuccess: () => void
}

export const Form = ({ id }: Props) => {
  const {
    attributes,
    setAttributes,
    save,
    saveSuccess,
    formBuilder,
    queryError,
    queryLoading,
    queryData,
    mutationLoading,
  } = useForm<UpdateMaintenanceEventMutation, MaintenanceEventAttributes, EditMaintenanceEventQuery, MaintenanceEvent>({
    id,
    initializer: me => ({
      assetId: me.asset.id,
      maintenanceTypeId: me.maintenanceType.id,
      startedAt: me.startedAt || null,
      state: me.state || null,
      result: me.result || null,
      details: me.details || '',
      tasks: me.maintenanceTasks.nodes.map(mt => ({
        maintenanceEventId: id,
        maintenanceTypeTaskId: mt.maintenanceTypeTask.id,
        result: mt.result
      })) || []
    }),
    query: EditQuery,
    queryKey: 'maintenanceEvents',
    mutation: UpdateMutation,
    resultKey: 'updateMaintenanceEvent'
  })

  const updateTaskResult = (taskId: string, result: string) => {
    setAttributes(attrs => {
      const tasks = attrs.tasks || []
      const task = tasks.find(t => t.maintenanceTypeTaskId == taskId)
      if (task) {
        task.result = result
      } else {
        tasks.push({ maintenanceEventId: id, maintenanceTypeTaskId: taskId, result })
      }
      return {
        ...attrs,
        tasks,
      }
    })
  }

  const finalize = () => {
    setAttributes(attrs => {
      return {
        ...attrs,
        state: MaintenanceEventState.Completed
      }
    })
    setTimeout(save, 100)
  }

  if (queryLoading) return <Loading />

  const extraData = queryData?.maintenanceEvents.nodes[0].extraData

  return (
    <>
      { queryError && <Error message={queryError} /> }
      <div className="flex flex-col gap-6">
        { formBuilder.buildQueryError() }
        { formBuilder.buildBaseError() }
        <div className="grid grid-cols-1 gap-6">
          { formBuilder.buildField({ name: 'Details', field: 'details', type: 'textarea' }) }
        </div>
        {
          extraData && <Field>
            <Label>Test data</Label>
            <div className="flex flex-col">
              { Object.keys(extraData).map(k => <div className="grid grid-cols-3 gap-4 text-sm font-mono"><span>{ k }</span><span className="col-span-2">{ extraData[k] }</span></div>) }
            </div>
          </Field>
        }
        <div className="grid grid-cols-1 gap-6">
          { formBuilder.buildField({ name: 'Result', field: 'result', type: 'maintenance-event-result' }) }
        </div>
        { queryData?.maintenanceEvents.nodes[0].maintenanceType.maintenanceTypeTasks.map(t => (
          <Field>
            <Label>{ t.name } { t.required && <span className="ml-2 text-zinc-500">(required)</span> }</Label>
            <Input
              type="text"
              value={attributes.tasks?.find(task => task.maintenanceTypeTaskId == t.id)?.result || ''}
              onChange={e => updateTaskResult(t.id, e.currentTarget.value)}
            />
          </Field>
        ))}
        <div className="flex flex-row gap-6 justify-end">
          { formBuilder.saveButton(save, mutationLoading, saveSuccess) }
          { attributes.state != MaintenanceEventState.Completed &&
            <span><Button color="blue" disabled={mutationLoading} onClick={finalize}>Finalize</Button></span> }
        </div>
      </div>
    </>
  )
}
